"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader, Zap, CircleCheck } from "lucide-react";
export default function ScaleYourContent() {
  return <section data-section-id="224054" id="join-the-club" className="bg-primary text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] sm:w-[800px] sm:h-[800px] bg-secondary/20 rounded-lg blur-[100px] animate-pulse pointer-events-none" />
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 pointer-events-none mix-blend-overlay" />

      <div className="container relative z-10 mx-auto px-4 max-w-3xl">
        <div className="text-center mb-12">
          <h2 className="font-default text-5xl sm:text-7xl font-bold tracking-tighter mb-4 uppercase text-secondary [text-shadow:4px_4px_0px_hsl(0,0%,15%)]">
            Send It Fit
          </h2>
          <p className="text-secondary-foreground font-default text-lg sm:text-xl max-w-2xl mx-auto">
            Get started with Send It Fit mobile personal training - customize your fitness program with certified trainers in Singapore
          </p>
        </div>

        <div className="bg-secondary backdrop-blur-none border-none rounded-2xl p-6 sm:p-10 shadow-2xl">
          <FormProvider formKey="join_club_form_home" formId="join_club_form_home" sectionName="scale-your-content" defaultValues={{
          name: "",
          email: "",
          phone: "",
          video_length: "",
          platforms: [],
          deadline: "",
          details: ""
        }} validationRules={{
          name: {
            required: "Name is required"
          },
          email: {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address"
            }
          },
          video_length: {
            required: "Training frequency is required",
            min: {
              value: 1,
              message: "Must be at least 1 session"
            }
          },
          platforms: {
            required: "Select at least one goal"
          }
        }} submitText="SEND IT" successMessage="Nice! We've received your details. One of our trainers will reach out soon to get you started.">
            <ScaleContentForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ScaleContentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-lg bg-primary/20 flex items-center justify-center">
          <CircleCheck className="size-10 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="font-default text-3xl font-bold text-foreground">Request Received</h3>
          <p className="text-foreground/80 font-default max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/50 hover:bg-primary/10 text-foreground font-default">
          Send Another Message
        </Button>
      </div>;
  }
  const inputClasses = "flex w-full rounded-md border-2 border-foreground bg-background/50 px-3 py-2 text-sm font-default placeholder:text-foreground/40 transition-all focus-visible:outline-none focus-visible:border-primary focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono uppercase tracking-wider text-xs">Your Name</FormLabel>
              <FormControl>
                <input type="text" className={`${inputClasses} h-12`} placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono uppercase tracking-wider text-xs">Email Address</FormLabel>
              <FormControl>
                <input type="email" className={`${inputClasses} h-12`} placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono uppercase tracking-wider text-xs">Phone Number <span className="text-foreground/50 text-[10px] font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <input type="tel" className={`${inputClasses} h-12`} placeholder="+65 8888 8888" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="video_length" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono uppercase tracking-wider text-xs">Training Frequency</FormLabel>
              <FormControl>
                <input type="number" min="1" className={`${inputClasses} h-12`} placeholder="3" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="platforms" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-mono uppercase tracking-wider text-xs">Fitness Goals</FormLabel>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              {["Weight Loss", "Muscle Gain", "General Health"].map((platform, i) => <label key={platform} data-index={i} className={`flex items-center space-x-3 rounded-md border-2 p-3 cursor-pointer transition-all ${field.value?.includes(platform) ? "border-primary bg-primary/10" : "border-foreground bg-background/50 hover:bg-background/80"}`}>
                  <input type="checkbox" className="sr-only" checked={field.value?.includes(platform) || false} onChange={e => {
            const current = field.value || [];
            const updated = e.target.checked ? [...current, platform] : current.filter((v: string) => v !== platform);
            field.onChange(updated);
          }} />
                  <div className={`size-4 rounded border-2 flex items-center justify-center ${field.value?.includes(platform) ? "border-primary bg-primary" : "border-foreground"}`}>
                    {field.value?.includes(platform) && <CircleCheck className="size-3 text-secondary" />}
                  </div>
                  <span className="text-sm font-mono uppercase font-bold">{platform}</span>
                </label>)}
            </div>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="deadline" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-mono uppercase tracking-wider text-xs">Preferred Start Date <span className="text-foreground/50 text-[10px] font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <input type="date" className={`${inputClasses} h-12 [color-scheme:light]`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-mono uppercase tracking-wider text-xs">Tell us about yourself <span className="text-foreground/50 text-[10px] font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <textarea className={`${inputClasses} min-h-[120px] resize-y`} placeholder="Any injuries or specific targets we should know about?" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border-2 border-destructive rounded-md text-destructive text-sm font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-xl sm:text-2xl font-default font-bold tracking-widest bg-foreground hover:bg-foreground/90 text-secondary active:shadow-none active:translate-x-[2px] active:translate-y-[2px] transition-all duration-150 rounded-lg group">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-3 size-6" />
            SENDING...
          </> : <>
            <Zap className="mr-3 size-6 group-hover:scale-110 transition-transform" />
            SEND IT
          </>}
      </Button>
    </Form>;
}